import { Component, inject } from '@angular/core';
import { EChartsOption } from 'echarts';
import { ITab } from '../../components/section-tabs/section-tabs.component';
import { ExportService, IExportBlock } from '../../services/export.service';
import { FilterService, IFilterContext } from '../../services/filter.service';
import { FILTROS_BASE, FILTROS_DEFAULT, FILTROS_TEXT_ANALYTICS } from '../../data/filters.data';
import { DRIVERS_RECOMENDACION } from '../../data/home.data';
import {
  CONSIDERACION,
  DETALLE_ENCUESTAS,
  IVariableCsat,
  historicoDe,
} from '../../data/results.data';
import {
  COLUMNAS_AREA,
  DESVIACION_HISTORICO,
  DETALLE_AREA_POR_PREGUNTA,
  IAreaMencion,
  IDetalleArea,
  MESES_HISTORICO,
  PREGUNTAS_ENCUESTA,
  PREGUNTA_DEFAULT,
} from '../../data/store-area.data';
import {
  COLUMNAS_DETALLE,
  COLUMNAS_PALANCAS,
  COMENTARIOS_DETALLE,
  FILAS_PALANCAS,
  IComentarioRow,
  IOrientacionSerie,
  IPalancaRow,
  MENCION_POR_PALANCA,
  ORIENTACION_POR_PALANCA,
  PALANCAS,
} from '../../data/text-analytics.data';
import { FilterField } from '../../models/filter-option.model';
import { ISeriesPoint } from '../../models/series-point.model';
import { TableColumn } from '../../models/table-column.model';
import { ChartService } from '../../services/chart.service';

/**
 * Voz del cliente (`/voz`) — responde «¿por qué?».
 *
 * Reúne en cuatro pestañas lo que antes vivía disperso en Resultados
 * Generales, Text Analytics y Detalle área por tienda: qué pesa en la
 * recomendación (drivers), cómo salen los atributos de la experiencia, qué
 * dicen los comentarios abiertos y qué área de la tienda señalan los
 * detractores de cada pregunta.
 */
@Component({
  selector: 'app-voice-page',
  standalone: false,
  templateUrl: './voice-page.component.html',
  styleUrl: './voice-page.component.scss',
})
export class VoicePageComponent {

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR navegación                         */
  /* ------------------------------------------------------------------ */

  public readonly tabs: Array<ITab> = [
    { id: 'drivers',     label: 'Drivers',     icon: 'bi-bar-chart-steps' },
    { id: 'atributos',   label: 'Atributos',   icon: 'bi-list-check', count: DETALLE_ENCUESTAS.length },
    { id: 'comentarios', label: 'Comentarios', icon: 'bi-chat-quote',  count: FILAS_PALANCAS.length },
    { id: 'area',        label: 'Por área',    icon: 'bi-shop' },
  ];

  public tab: string = 'drivers';

  /**
   * La sección hereda los filtros de las tres pantallas que absorbe: el juego
   * base más la Orientación que sólo traía Text Analytics. Se compone de las
   * constantes existentes, no se declara un campo nuevo.
   */
  public readonly filtros: Array<FilterField> = [
    ...FILTROS_BASE,
    ...FILTROS_TEXT_ANALYTICS.filter((campo) => campo.key === 'orientacion'),
  ];

  public seleccion: Record<string, Array<string>> = { ...FILTROS_DEFAULT };

  private readonly filterService: FilterService = inject(FilterService);
  private readonly exportService: ExportService = inject(ExportService);

  private ctx: IFilterContext = this.filterService.contexto(this.seleccion);

  /** Qué está acotando la vista; vacío cuando no hay filtros de universo. */
  public alcance: string = '';

  /* ------------------------------------------------------------------ */
  /*                            ANCHOR drivers                          */
  /* ------------------------------------------------------------------ */

  public drivers!: EChartsOption;
  public consideracion!: EChartsOption;

  /* Los datos de cada gráfica, además de su `EChartsOption`: una opción de
     ECharts no se puede exportar, los valores quedan enterrados entre
     degradados y formateadores. */
  private driversDatos: Array<{ name: string; value: number }> = [];
  private consideracionDatos: Array<{ name: string; value: number }> = [];
  private mencionDatos: Array<ISeriesPoint> = [];
  private orientacionDatos: Array<{ name: string; data: Array<number> }> = [];

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR atributos                         */
  /* ------------------------------------------------------------------ */

  public atributos: Array<IVariableCsat> = DETALLE_ENCUESTAS;

  /* Los tres juegos de botones viven como campos y no como literales en la
     plantilla: un arreglo nuevo en cada ciclo de detección volvería a disparar
     el ngOnChanges de <app-data-table>. */
  public readonly exportacion: Array<string> = ['Copiar', 'CSV', 'Excel', 'PDF'];
  public readonly exportacionArea: Array<string> = ['Copiar', 'CSV', 'PDF'];
  public readonly sinExportacion: Array<string> = [];

  public readonly columnasAtributos: Array<TableColumn> = [
    { key: 'variable',    title: 'VARIABLE' },
    { key: 'detractores', title: 'DETRACTORES', headerStyle: 'detractor', cellStyle: 'center', suffix: '%' },
    { key: 'pasivos',     title: 'PASIVOS',     headerStyle: 'pasivo',    cellStyle: 'center', suffix: '%' },
    { key: 'promotores',  title: 'PROMOTORES',  headerStyle: 'promotor',  cellStyle: 'center', suffix: '%' },
    { key: 'csat',        title: 'CSAT',        cellStyle: 'nps' },
    { key: 'historico',   title: 'HISTÓRICO',   cellStyle: 'icon', icon: 'bi-bar-chart-line-fill', sortable: false },
  ];

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR comentarios                        */
  /* ------------------------------------------------------------------ */

  public mencionPorPalanca!: EChartsOption;
  public orientacionPorPalanca!: EChartsOption;

  public readonly columnasPalancas: Array<TableColumn> = COLUMNAS_PALANCAS;
  public filasPalancas: Array<IPalancaRow> = FILAS_PALANCAS;
  public readonly columnasComentarios: Array<TableColumn> = COLUMNAS_DETALLE;

  /** Comentarios que sobreviven al filtro; sostienen el modal de cada palanca. */
  private comentarios: Array<IComentarioRow> = COMENTARIOS_DETALLE;

  public comentariosAbiertos: boolean = false;
  public filasComentarios: Array<IComentarioRow> = [];

  /* ------------------------------------------------------------------ */
  /*                             ANCHOR área                            */
  /* ------------------------------------------------------------------ */

  public readonly bannerArea: string =
    'DETRACTORES - ÁREA DE LA TIENDA QUE LES GENERÓ MALA EXPERIENCIA';

  public readonly preguntas: Array<string> = PREGUNTAS_ENCUESTA;
  public readonly columnasArea: Array<TableColumn> = COLUMNAS_AREA;

  public preguntaSeleccionada: string = PREGUNTA_DEFAULT;

  /** Área con más menciones de la pregunta vigente: la que abre la desviación. */
  public areaDestacada: string = '';
  public desviacionHistorica!: EChartsOption;

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR modal histórico                      */
  /* ------------------------------------------------------------------ */

  public historicoAbierto: boolean = false;
  public historicoTitulo: string = '';
  public historicoPuntos: Array<ISeriesPoint> = [];

  constructor(private readonly chartService: ChartService) {
    this.aplicar();
  }

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR derivados                         */
  /* ------------------------------------------------------------------ */

  /**
   * Bloque de menciones de la pregunta vigente, ya derivado.
   *
   * Se recalcula en `aplicar()` y al cambiar de pregunta, no en un getter: la
   * tabla y la gráfica leen esto en cada ciclo de detección y devolver un
   * objeto nuevo cada vez reiniciaría la paginación.
   */
  public detalleArea: IDetalleArea = DETALLE_AREA_POR_PREGUNTA[PREGUNTA_DEFAULT];

  public get filasArea(): Array<IAreaMencion> {
    return this.detalleArea.filas;
  }

  public get detractores(): number {
    return this.detalleArea.detractores;
  }

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR acciones                          */
  /* ------------------------------------------------------------------ */

  public onTab(id: string): void {
    this.tab = id;
    this.cerrarTodo();
  }

  public onFiltros(valores: Record<string, Array<string>>): void {
    this.seleccion = valores;
    this.ctx = this.filterService.contexto(valores);
    this.cerrarTodo();
    this.aplicar();
  }

  public onPregunta(pregunta: string): void {
    this.preguntaSeleccionada = pregunta;
    this.detalleArea = this.derivarArea();
    this.desviacionHistorica = this.construirDesviacion();
    this.cerrarTodo();
  }

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR recálculo                         */
  /* ------------------------------------------------------------------ */

  /**
   * Rehace la sección contra el contexto vigente.
   *
   * Los comentarios sí traen región, zona y sucursal, así que se filtran de
   * verdad. El resto de esta sección —drivers, consideración, atributos,
   * palancas y áreas— son agregados que el programa publica sin ninguna
   * dimensión: no hay renglón por encuesta del que recalcularlos, y se derivan
   * de forma determinista a partir de la selección.
   */
  private aplicar(): void {
    const ctx = this.ctx;

    this.alcance = this.filterService.resumenAlcance(ctx);

    /* --- drivers ------------------------------------------------------ */
    const pesosDrivers = this.filterService.reparto(
      DRIVERS_RECOMENDACION.map((d) => d.value), ctx, 'drivers');

    this.driversDatos = DRIVERS_RECOMENDACION.map((driver, i) => ({ ...driver, value: pesosDrivers[i] }));
    this.drivers = this.chartService.stackedBar(this.driversDatos);

    this.consideracionDatos = CONSIDERACION.map((fila) => ({
      ...fila,
      value: this.filterService.desviar(fila.value, ctx, `consid:${fila.name}`, 6, 0, 100),
    }));
    this.consideracion = this.chartService.column(
      this.consideracionDatos,
      ChartService.COLORS.teal,
    );

    /* --- atributos ---------------------------------------------------- */
    this.atributos = DETALLE_ENCUESTAS.map((fila) => {
      const detractores = this.filterService.desviar(fila.detractores, ctx, `atr:${fila.variable}:d`, 4, 1, 40);
      const pasivos = this.filterService.desviar(fila.pasivos, ctx, `atr:${fila.variable}:p`, 5, 5, 45);

      return {
        ...fila,
        detractores,
        pasivos,
        promotores: 100 - detractores - pasivos,
        csat: this.filterService.desviar(fila.csat, ctx, `atr:${fila.variable}:c`, 6, 10, 99),
      };
    });

    /* --- comentarios -------------------------------------------------- */
    this.comentarios = this.filterService.filas(
      COMENTARIOS_DETALLE, this.seleccion, ['region', 'zona', 'sucursal']);

    const pesosPalancas = this.filterService.reparto(
      FILAS_PALANCAS.map((fila) => fila.mencionesPct), ctx, 'palancas');

    this.filasPalancas = FILAS_PALANCAS.map((fila, i) => ({
      ...fila,
      menciones: Math.max(1, Math.round(fila.menciones * this.filterService.factorMuestra(ctx))),
      mencionesPct: pesosPalancas[i],
    }));

    this.mencionPorPalanca = this.construirMencion();
    this.orientacionDatos = ORIENTACION_POR_PALANCA.map((serie) => ({
      ...serie,
      data: serie.data.map((valor, i) =>
        this.filterService.desviar(valor, ctx, `orient:${serie.name}:${i}`, 8, 0, 100)),
    }));
    this.orientacionPorPalanca = this.chartService.groupedColumn(
      PALANCAS,
      this.orientacionDatos as Array<IOrientacionSerie>,
    );

    /* --- por área ----------------------------------------------------- */
    this.detalleArea = this.derivarArea();
    this.desviacionHistorica = this.construirDesviacion();
  }

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR exportación                        */
  /* ------------------------------------------------------------------ */

  /** Contexto que cada `<app-data-table>` escribe en la cabecera del archivo. */
  public get filtrosTexto(): string {
    return this.filterService.descripcion(this.seleccion, this.filtros);
  }

  private get tituloPestana(): string {
    return this.tabs.find((pestana) => pestana.id === this.tab)?.label ?? 'Voz del cliente';
  }

  /** Baja la pestaña abierta, con las gráficas convertidas en tabla. */
  public descargar(): void {
    this.exportService.csv(
      this.bloques(),
      this.exportService.nombreArchivo('nps', 'voz', this.tituloPestana),
      {
        titulo: `Voz del cliente · ${this.tituloPestana}`,
        filtros: this.filtrosTexto,
      },
    );
  }

  /** El botón del panel de Atributos: sólo esa tabla. */
  public descargarAtributos(): void {
    this.exportService.csv(
      [this.bloqueAtributos()],
      this.exportService.nombreArchivo('nps', 'atributos'),
      { titulo: 'Atributos de la experiencia', filtros: this.filtrosTexto },
    );
  }

  private bloques(): Array<IExportBlock> {
    switch (this.tab) {
      case 'atributos':
        return [this.bloqueAtributos()];

      case 'comentarios':
        return [
          {
            titulo: 'Mención por palanca',
            columnas: [
              { key: 'label', title: 'Palanca' },
              { key: 'value', title: 'Mención', suffix: '%' },
            ],
            filas: this.mencionDatos,
          },
          {
            titulo: 'Orientación por palanca',
            columnas: [
              { key: 'palanca', title: 'Palanca' },
              ...this.orientacionDatos.map((serie) => ({ key: serie.name, title: serie.name })),
            ],
            // La gráfica es una serie por orientación; la tabla es una fila por
            // palanca, que es como se lee un archivo.
            filas: PALANCAS.map((palanca, i) => this.orientacionDatos.reduce(
              (fila, serie) => ({ ...fila, [serie.name]: serie.data[i] ?? 0 }),
              { palanca } as Record<string, any>,
            )),
          },
          {
            titulo: 'Palancas detectadas',
            columnas: [
              { key: 'palanca', title: 'Palanca' },
              { key: 'menciones', title: 'Menciones' },
              { key: 'mencionesPct', title: 'Menciones', suffix: '%' },
            ],
            filas: this.filasPalancas,
          },
        ];

      case 'area':
        return [
          {
            titulo: `Menciones por área · ${this.preguntaSeleccionada}`,
            columnas: [
              { key: 'variable', title: 'Variable' },
              { key: 'menciones', title: 'Menciones' },
              { key: 'porcentajeValor', title: 'Menciones', suffix: '%' },
            ],
            filas: this.filasArea,
          },
          {
            titulo: `Desviación histórica · ${this.areaDestacada}`,
            columnas: [
              { key: 'label', title: 'Mes' },
              { key: 'value', title: 'Menciones', suffix: '%' },
            ],
            filas: this.serieDesviacion(this.areaConMasMenciones().porcentajeValor),
          },
        ];

      default:
        return [
          {
            titulo: 'Drivers de recomendación',
            columnas: [
              { key: 'name', title: 'Driver' },
              { key: 'value', title: 'Peso', suffix: '%' },
            ],
            filas: this.driversDatos,
          },
          {
            titulo: 'Consideración',
            columnas: [
              { key: 'name', title: 'Motivo' },
              { key: 'value', title: 'Porcentaje', suffix: '%' },
            ],
            filas: this.consideracionDatos,
          },
        ];
    }
  }

  private bloqueAtributos(): IExportBlock {
    return {
      titulo: 'Atributos de la experiencia',
      columnas: [
        { key: 'variable', title: 'Variable' },
        { key: 'detractores', title: 'Detractores', suffix: '%' },
        { key: 'pasivos', title: 'Pasivos', suffix: '%' },
        { key: 'promotores', title: 'Promotores', suffix: '%' },
        { key: 'csat', title: 'CSAT', suffix: '%' },
      ],
      filas: this.atributos,
    };
  }

  /** Menciones por área de la pregunta vigente, derivadas del contexto. */
  private derivarArea(): IDetalleArea {
    const base = DETALLE_AREA_POR_PREGUNTA[this.preguntaSeleccionada]
      ?? DETALLE_AREA_POR_PREGUNTA[PREGUNTA_DEFAULT];
    const ctx = this.ctx;

    if (!this.filterService.deriva(ctx)) {
      return base;
    }

    const pregunta = this.preguntaSeleccionada;
    const menciones = this.filterService.reparto(
      base.filas.map((fila) => fila.menciones), ctx, `area:${pregunta}`);
    const total = menciones.reduce((suma, valor) => suma + valor, 0) || 1;

    return {
      detractores: Math.max(1, Math.round(base.detractores * this.filterService.factorMuestra(ctx))),
      filas: base.filas.map((fila, i) => {
        const porcentajeValor = Math.round((menciones[i] / total) * 10_000) / 100;

        return {
          ...fila,
          menciones: menciones[i],
          porcentaje: porcentajeValor.toFixed(2),
          porcentajeValor,
        };
      }),
    };
  }

  /** Columna HISTÓRICO de la pestaña Atributos: 19 meses alrededor del CSAT. */
  public abrirHistoricoAtributo(evento: { row: any; column: TableColumn }): void {
    const fila = evento.row as IVariableCsat;

    this.historicoTitulo = `Histórico: ${fila.variable}`;
    this.historicoPuntos = historicoDe(fila.csat);
    this.historicoAbierto = true;
  }

  /** Columna HISTÓRICO de la pestaña Por área: 19 meses del % de menciones. */
  public abrirHistoricoArea(evento: { row: any; column: TableColumn }): void {
    const fila = evento.row as IAreaMencion;

    this.historicoTitulo = `Histórico: ${fila.variable}`;
    this.historicoPuntos = this.serieDesviacion(fila.porcentajeValor);
    this.historicoAbierto = true;
  }

  public cerrarHistorico(): void {
    this.historicoAbierto = false;
  }

  /** Icono Detalle de la tabla de palancas: los encuestados que la mencionan. */
  public abrirComentarios(evento: { row: any; column: TableColumn }): void {
    const palanca: string = (evento.row as IPalancaRow).palanca;

    this.filasComentarios = this.comentarios.map((fila) => ({ ...fila, palanca }));
    this.comentariosAbiertos = true;
  }

  public cerrarComentarios(): void {
    this.comentariosAbiertos = false;
  }

  private cerrarTodo(): void {
    this.comentariosAbiertos = false;
    this.historicoAbierto = false;
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR construirMencion                     */
  /* ------------------------------------------------------------------ */
  /**
   * Las etiquetas del eje son nombres completos de palanca: se reduce la
   * tipografía y se acota el ancho para que el texto corte en varias líneas
   * en vez de invadir la gráfica.
   */
  private construirMencion(): EChartsOption {
    const pesos = this.filterService.reparto(
      MENCION_POR_PALANCA.map((punto) => punto.value), this.ctx, 'mencion');

    this.mencionDatos = MENCION_POR_PALANCA.map((punto, i) => ({ ...punto, value: pesos[i] }));

    const opciones = this.chartService.npsLine(
      this.mencionDatos,
      ChartService.COLORS.teal,
    ) as any;

    opciones.grid = { ...opciones.grid, bottom: 96, top: 50 };
    opciones.xAxis = {
      ...opciones.xAxis,
      axisLabel: {
        ...opciones.xAxis.axisLabel,
        fontSize: 10,
        lineHeight: 12,
        overflow: 'break',
        width: 92,
      },
    };

    return opciones as EChartsOption;
  }

  /* ------------------------------------------------------------------ */
  /*                      ANCHOR construirDesviacion                    */
  /* ------------------------------------------------------------------ */
  /**
   * Serie visible de la pestaña Por área. Se arma una vez por pregunta y no
   * en un getter: un `EChartsOption` nuevo en cada ciclo de detección haría
   * que la gráfica se reconstruyera sin parar.
   */
  private construirDesviacion(): EChartsOption {
    const fila = this.areaConMasMenciones();

    this.areaDestacada = fila.variable;

    return this.chartService.npsLine(
      this.serieDesviacion(fila.porcentajeValor),
      ChartService.COLORS.teal,
      45,
    );
  }

  private areaConMasMenciones(): IAreaMencion {
    return this.filasArea.reduce(
      (mayor, fila) => (fila.menciones > mayor.menciones ? fila : mayor),
      this.filasArea[0],
    );
  }

  /** 19 meses alrededor del porcentaje actual de la fila. */
  private serieDesviacion(base: number): Array<ISeriesPoint> {
    return MESES_HISTORICO.map((mes, indice) => {
      const valor = Math.max(0, base + (DESVIACION_HISTORICO[indice] ?? 0));

      return { label: mes, value: Math.round(valor * 100) / 100 };
    });
  }
}
